import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
import { LocationCurrentnessStatus } from 'api/swagger/ApiClient';
import { autoinject, BindingEngine } from 'aurelia-framework';
import { UserLocationDataMarkerController } from 'components/locations/user-location-data-marker-controller';
import { DateTimeValueConverter } from 'framework/value_converters/date-and-time';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { UserLocation } from 'model/users-locations';
import { UsersLocationsController } from 'screens/administration/locations/users-locations-controller';
import { DataMarker, esriApiKey } from 'support/map';
import { TaskMutex } from '../../../framework/script_utilities/task-executor';
import { Site } from '../../../model/business-keywords';
import { KeywordsService } from '../../../services/keywords-service';

@autoinject
export class UsersLocationsMap {
  private locationsMapPlaceholder: HTMLElement;
  private filterBox: HTMLElement;
  private map: L.Map;
  private resizeObserver: ResizeObserver;
  private mapMinZoom = 2;
  private mapMaxZoom = 18;
  private mapFilterMaxZoomDelta = 4;
  private userLocationsMarkersGroup: L.MarkerClusterGroup;
  private userLocationsMarkersMap: Map<string, UserLocationDataMarkerController> = new Map();
  private sitesMarkersGroup: L.MarkerClusterGroup;
  private sitesMarkersMap: Map<number, DataMarker<Site>> = new Map();
  private siteOrLocationCircle: L.Circle = null;
  private mapZone: L.GeoJSON = null;
  private defaultMapCursor: string = null;
  private sharedLocationMarkerIconOptions = {
    shadowUrl: 'images/marker-shadow.png',
    iconSize: [25, 41] as L.PointExpression,
    iconAnchor: [12, 41] as L.PointExpression,
    popupAnchor: [1, -38] as L.PointExpression,
    tooltipAnchor: [16, -28] as L.PointExpression,
    shadowSize: [41, 41] as L.PointExpression
  }
  private sharedSiteMarkerIconOptions = {
    iconSize: [40, 40] as L.PointExpression,
    iconAnchor: [20, 20] as L.PointExpression,
    popupAnchor: [0, -18] as L.PointExpression
  }
  currentLocationMarkerIcon = L.icon({
    ...this.sharedLocationMarkerIconOptions,
    iconUrl: 'images/marker-blue-icon.png'
  });
  lastDayLocationMarkerIcon = L.icon({
    ...this.sharedLocationMarkerIconOptions,
    iconUrl: 'images/marker-light-blue-icon.png'
  });
  lastMonthLocationMarkerIcon = L.icon({
    ...this.sharedLocationMarkerIconOptions,
    iconUrl: 'images/marker-lighter-blue-icon.png'
  });
  private highlightMarkerIcon = L.icon({
    ...this.sharedLocationMarkerIconOptions,
    iconUrl: 'images/marker-yellow-icon.png'
  });

  constructor(private controller: UsersLocationsController, private keywords: KeywordsService, private bindingEngine: BindingEngine, private dateTimeValueConverter: DateTimeValueConverter, private mutex: TaskMutex) {
  }

  /* Lifecycle functions */

  attached() {
    this.controller.showUserLocationDetailInMap = (userLocation: UserLocation) => this.showUserLocationDetailInMap(userLocation);
    this.controller.zoomUserLocationInMap = (userLocation: UserLocation) => this.zoomUserLocationInMap(userLocation);
    this.controller.showNewSearchInMap = async () => await this.showNewSearchInMap();
    this.controller.getUsersLocationsInView = () => this.getUsersLocationsInView();
    this.controller.getUsersLocationsOutsideView = () => this.getUsersLocationsOutsideView();
    this.controller.changeSitesVisibility = (areDisplayed: boolean) => this.changeSitesVisibility(areDisplayed);
    this.controller.showSiteOrLocationInMap = (site: Site, selectedLocation: L.LatLng, distanceInMeters: number) => this.showSiteOrLocation(site, selectedLocation, distanceInMeters);
    this.controller.toggleLocationSelectingInMap = (isLocationSelecting) => this.toggleLocationSelecting(isLocationSelecting);
    this.createMap();
    this.defaultMapCursor = this.locationsMapPlaceholder.style.cursor;
    if (this.controller.usersLocationsMap && this.controller.usersLocationsMap.length > 0)
      this.addAllUsersLocationsMarkers();
  }

  detached() {
    this.controller.mapCenter = this.map.getCenter();
    this.controller.mapZoom = this.map.getZoom();
    this.controller.showUserLocationDetailInMap = null;
    this.controller.showNewSearchInMap = null;
    this.controller.zoomUserLocationInMap = null;
    this.controller.getUsersLocationsInView = null;
    this.controller.getUsersLocationsOutsideView = null;
    this.controller.changeSitesVisibility = null;
    this.controller.showSiteOrLocationInMap = null;
    this.controller.toggleLocationSelectingInMap = null;
    this.resizeObserver.disconnect();
    this.removeAllUsersLocationsMarkers();
  }

  /* Map functions */

  private async createMap() {
    if (this.map == null) {
      this.map = L.map(this.locationsMapPlaceholder, {} as L.MapOptions);
      this.setMapPreviousView(this.map);
      L.esri.Vector.vectorTileLayer(
        "https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer",
        {
          apiKey: esriApiKey,
          token: null,
          portalUrl: null,
          style: null
        }
      ).addTo(this.map);
      this.map.setMaxZoom(this.mapMaxZoom);
      this.map.setMinZoom(this.mapMinZoom);
      this.map.on("moveend", () => { this.controller.mapMoved(); });
      this.userLocationsMarkersGroup = L.markerClusterGroup({ disableClusteringAtZoom: this.map.getMaxZoom(), iconCreateFunction: (cluster: L.MarkerCluster) => this.usersLocationsGroupIconCreateFunction(cluster) } as L.MarkerClusterGroupOptions);
      this.userLocationsMarkersGroup
        .on('animationend', () => {
          const detailMarker = Array.from(this.userLocationsMarkersMap.values()).find(x => x.dataMarker.data.isDetail);
          if (detailMarker != null)
            detailMarker.refreshDetail();
        })
      this.map.addLayer(this.userLocationsMarkersGroup);
      this.sitesMarkersGroup = L.markerClusterGroup({ disableClusteringAtZoom: this.map.getMaxZoom(), iconCreateFunction: (cluster: L.MarkerCluster) => this.sitesGroupIconCreateFunction(cluster) } as L.MarkerClusterGroupOptions);
      this.map.addLayer(this.sitesMarkersGroup);
      this.addControl(this.map, this.filterBox, 'topright');
      this.map.on("click", (e) => { this.controller.mapClicked(e.latlng) });
    }
    const ResizeObserver = (<any>window).ResizeObserver || ResizeObserverPolyfill;
    this.resizeObserver = new ResizeObserver(() => { this.map.invalidateSize(); });
    this.resizeObserver.observe(this.locationsMapPlaceholder);
  }

  private async showNewSearchInMap() {
    this.removeAllUsersLocationsMarkers();
    this.addAllUsersLocationsMarkers();
    await this.showMapZone();
  }

  private toggleLocationSelecting(isLocationSelecting: boolean) {
    this.locationsMapPlaceholder.style.cursor = isLocationSelecting ? 'crosshair' : this.defaultMapCursor;
  }

  /* Users Locations functions */

  private showUserLocationDetailInMap(userLocation: UserLocation) {
    const detailMarkerController = userLocation.location != null ? this.userLocationsMarkersMap.get(userLocation.location.id.toLowerCase()) : null;
    if (detailMarkerController != null)
      this.map.setView({ lat: detailMarkerController.dataMarker.data.location.coordinates[0], lng: detailMarkerController.dataMarker.data.location.coordinates[1] } as L.LatLngLiteral, this.map.getZoom());
  }

  private zoomUserLocationInMap(userLocation: UserLocation) {
    const detailMarkerController = userLocation.location != null ? this.userLocationsMarkersMap.get(userLocation.location.id.toLowerCase()) : null;
    if (detailMarkerController != null)
      this.map.setView({ lat: detailMarkerController.dataMarker.data.location.coordinates[0], lng: detailMarkerController.dataMarker.data.location.coordinates[1] } as L.LatLngLiteral, this.getFilterZoomLevel());
  }

  private getUsersLocationsInView(): UserLocation[] {
    const mapBounds = this.map.getBounds();
    return Array.from(this.userLocationsMarkersMap.values()).filter(markerController => mapBounds.contains(markerController.dataMarker.getLatLng())).map(x => x.dataMarker.data);
  }

  private getUsersLocationsOutsideView(): UserLocation[] {
    const mapBounds = this.map.getBounds();
    return Array.from(this.userLocationsMarkersMap.values()).filter(markerController => !mapBounds.contains(markerController.dataMarker.getLatLng())).map(x => x.dataMarker.data);
  }

  private addAllUsersLocationsMarkers() {
    const newMarkers = this.controller.usersLocationsMap
      .map(userLocation => {
        const marker = new DataMarker<UserLocation>(new L.LatLng(userLocation.location.coordinates[0], userLocation.location.coordinates[1]), userLocation, { title: userLocation.user.fullName ? userLocation.user.fullName : userLocation.user.loginName, icon: this.getUserLocationStandardMarkerIcon(userLocation) });
        marker
          .on("click", () => { this.controller.detailDisplayedInMap(marker.data); })
          .on('mouseover', () => { marker.data.isHighlighted = true; })
          .on('mouseout', () => { marker.data.isHighlighted = false; });
        return marker;
      });
    newMarkers.forEach(x => {
      const markerController = new UserLocationDataMarkerController(this.bindingEngine, x, this.userLocationsMarkersGroup, {
        getStandardMarkerIcon: (userLocation) => this.getUserLocationStandardMarkerIcon(userLocation),
        highlightMarkerIcon: this.highlightMarkerIcon,
        popupContent: this.getUserLocationMarkerPopup(x.data)
      });
      this.userLocationsMarkersMap.set(x.data.location.id.toLowerCase(), markerController);
    });
    this.userLocationsMarkersGroup.addLayers(newMarkers, true);
    Array.from(this.userLocationsMarkersMap.values()).forEach(markerController => markerController.attach());
  }

  private removeAllUsersLocationsMarkers() {
    const removeMarkers = this.userLocationsMarkersGroup.getLayers().map(x => x as DataMarker<UserLocation>);
    removeMarkers.forEach(x => {
      const markerController = this.userLocationsMarkersMap.get(x.data.location.id.toLowerCase());
      markerController.unattach();
      markerController.dataMarker
        .off('click')
        .off('mouseover')
        .off('mouseout');
      this.userLocationsMarkersMap.delete(x.data.location.id.toLowerCase());
    });
    this.userLocationsMarkersGroup.removeLayers(this.userLocationsMarkersGroup.getLayers());
  }

  /* Sites functions */

  private changeSitesVisibility(areDisplayed: boolean) {
    if (areDisplayed)
      this.addAllSitesMarkers();
    else
      this.removeAllSitesMarkers();
  }

  private addAllSitesMarkers() {
    const newMarkers = this.keywords.sites
      .map(site => {
        const marker = new DataMarker<Site>(new L.LatLng(site.latitude, site.longitude), site, { 
          title: site.name, 
          icon: L.icon({
            ...this.sharedSiteMarkerIconOptions,
            iconUrl: `images/${site.icon}`
          })
        });
        marker.bindPopup(this.getSiteMarkerPopup(marker.data))
          .on("popupopen", (a) => {
            var popUp = a.target.getPopup();
            var requestButton = popUp.getElement().querySelector(".siteSearch");
            requestButton.addEventListener("click", e => {
              this.mutex.executeIfNotRunning(() => {
                const siteId = Number.parseInt(requestButton.attributes.getNamedItem("data-siteId").value);
                const site = this.keywords.sites.find(x => x.id === siteId);
                if (site != null)
                  return this.controller.searchBySite(site);
              });
            });
          });
        this.sitesMarkersMap.set(site.id, marker);
        return marker;
      });
    this.sitesMarkersGroup.addLayers(newMarkers, true);
  }

  private removeAllSitesMarkers() {
    const removeMarkers = this.sitesMarkersGroup.getLayers().map(x => x as DataMarker<Site>);
    removeMarkers.forEach(x => {
      this.sitesMarkersMap.delete(x.data.id);
    });
    this.sitesMarkersGroup.removeLayers(this.sitesMarkersGroup.getLayers());
  }

  private showSiteOrLocation(site: Site, selectedLocation: L.LatLng, distanceInMeters: number) {
    if (this.siteOrLocationCircle != null) {
      this.map.removeLayer(this.siteOrLocationCircle);
      this.siteOrLocationCircle = null;
    }
    if (site || selectedLocation)
      this.siteOrLocationCircle = this.getCircle(site ? site.latitude : selectedLocation.lat, site ? site.longitude : selectedLocation.lng, distanceInMeters).addTo(this.map);
  }

  /* MapZone functions */

  private async showMapZone() {
    if (this.mapZone != null)
      this.mapZone.removeFrom(this.map);
    const mapZoneData = await this.controller.getMapZone();
    if (mapZoneData != null) {
      this.mapZone = L.geoJSON(JSON.parse(mapZoneData.featureCollection))
      this.mapZone.addTo(this.map);
    }
  }

  /* Helper functions */

  private addControl(map: L.Map, element: HTMLElement, position: L.ControlPosition) {
    const customControl = new (L.Control.extend({
      element: undefined,
      onAdd() {
        return this.element;
      },
      setElement(el) {
        this.element = el;
      }
    }))();
    customControl.setPosition(position)
    customControl.setElement(element);
    L.DomEvent.disableClickPropagation(element);
    L.DomEvent.disableScrollPropagation(element);
    map.addControl(customControl);
  }

  private setMapPreviousView(map: L.Map) {
    if (this.controller.mapZoom && this.controller.mapCenter)
      map.setView(this.controller.mapCenter, this.controller.mapZoom);
    else
      this.setMapDefaultView(map);
  }

  private setMapDefaultView(map: L.Map) {
    map.setView([38.272689, 10.810547], 3);
  }

  private getFilterZoomLevel() {
    return this.map.getMaxZoom() - this.mapFilterMaxZoomDelta;
  }

  private getUserLocationMarkerPopup(userLocation: UserLocation): string {
    return `<div><label>Name:</label> ${userLocation.user.fullName}</div><div><label>Email:</label> ${userLocation.user.email ? userLocation.user.email : ''}</div><div><label>Tracked on:</label> ${this.dateTimeValueConverter.toView(userLocation.location.timeStamp)}</div>`;
  }

  private getUserLocationStandardMarkerIcon(userLocation: UserLocation): L.Icon {
    switch (userLocation.location.currentnessStatus) {
      case LocationCurrentnessStatus.OneHour:
        return this.currentLocationMarkerIcon;
      case LocationCurrentnessStatus.OneDay:
        return this.lastDayLocationMarkerIcon;
      default:
        return this.lastMonthLocationMarkerIcon;
    }
  }

  private getSiteMarkerPopup(site: Site): string {
    return `<div class="site-markup-popup"><div><span class="site-markup-popup__row-label">Name:</span><span>${site.name}</span></div><div><span class="site-markup-popup__row-label">Address:</span><span>${site.address ? site.address : ''}</span></div><div><span class="site-markup-popup__row-label">Asset type:</span><span>${site.assetType ? site.assetType : ''}</span></div><div><span class="site-markup-popup__row-label">Operation:</span><span>${site.operation ? site.operation : ''}</span></div><div class="d-flex justify-content-center"><button type="button" class="siteSearch" data-siteId="${site.id}">Search nearby users</button></div></div>`;
  }

  private usersLocationsGroupIconCreateFunction(cluster: L.MarkerCluster): L.DivIcon {
    return this.groupIconCreateFunction(cluster, 'users-locations');
  }

  private sitesGroupIconCreateFunction(cluster: L.MarkerCluster): L.DivIcon {
    return this.groupIconCreateFunction(cluster, 'sites');
  }

  private groupIconCreateFunction(cluster: L.MarkerCluster, type: string): L.DivIcon {
    var childCount = cluster.getChildCount();
    return new L.DivIcon({ html: '<div><span>' + childCount + '</span></div>', className: 'marker-cluster marker-cluster-' + type, iconSize: new L.Point(40, 40) });
  }

  private getCircle(latitude: number, longitude: number, distanceInMeters: number) {
    return L.circle([latitude, longitude], {
      color: 'red',
      opacity: 0.8,
      weight: 2,
      fillColor: '#f03',
      fillOpacity: 0.1,
      radius: distanceInMeters
    })
  }
}
